import { FC, memo, useMemo } from 'react';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { Flex } from 'antd';
import { CellContext, createColumnHelper } from '@tanstack/react-table';
import translations from './SystemClassifierItemTable.translations.json';
import { Table, SystemClassifierItemDto } from '@atlas-frontend/sdk';

const { addTranslations, useTranslation } = i18nNsFactory(
  'system-classifier-item-table'
);
addTranslations(translations);

const columnHelper = createColumnHelper<SystemClassifierItemDto>();

export type SystemClassifierItemTableProps = {
  systemClassifierItems: SystemClassifierItemDto[];
};

export const SystemClassifierItemTable: FC<SystemClassifierItemTableProps> = (
  props
) => {
  const { systemClassifierItems } = props;
  const { t } = useTranslation();

  const columns = useMemo(
    () => [
      columnHelper.accessor('sequenceNumber', {
        header: t('column.sequenceNumber'),
        meta: { width: '14ch', className: 'sticky-left', textAlign: 'right' },
        sortingFn: 'basic',
        filterFn: 'includesString',
      }),
      columnHelper.accessor('code', {
        header: t('column.code'),
        meta: { minWidth: '25ch', className: 'sticky-left' },
      }),
      columnHelper.accessor('name', {
        header: t('column.name'),
      }),
      columnHelper.accessor('systemValue', {
        header: t('column.systemValue'),
        cell: memo((props: CellContext<SystemClassifierItemDto, any>) => {
          const value = props.getValue();
          return t(value ? 'yes' : 'no');
        }),
        meta: { width: '17ch' },
        filterFn: 'includesOption',
      }),
      columnHelper.accessor('parameters', {
        header: t('column.parameters'),
      }),
    ],
    [t]
  );

  const options = useMemo(
    () => ({
      systemValue: [
        { id: true, name: t('yes') },
        { id: false, name: t('no') },
      ],
    }),
    [t]
  );

  return (
    <Flex align="end" gap="middle" vertical>
      <Table
        name="system-classifier-items-table"
        data={systemClassifierItems}
        columns={columns}
        options={options}
        defaultSort={[{ id: 'sequenceNumber', desc: false }]}
        sticky
        virtual
        scrollContainerSelector="#detail-layout-main-content"
      />
    </Flex>
  );
};
